import { trackUsage } from "@/lib/usage/track";
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  SAFETY_RULES_TEMPLATE,
  SAFETY_RULE_STATUS_LABELS,
} from "@/lib/basProcess/safetyRules";
import { Plus, Trash2, ShieldAlert, Sparkles, Undo2, Check, X } from "lucide-react";
import { DocumentHeader } from "@/components/BasProcess/DocumentHeader";

interface Props {
  projectId: string;
}

interface RuleDoc {
  id: string;
  name: string;
  status: string;
  project_specific: boolean;
  intro: string | null;
}

interface RuleItem {
  id: string;
  category: string;
  rule_key: string;
  text: string;
  generic_text: string | null;
  legal_reference: string | null;
  included: boolean;
  is_custom: boolean;
  ai_suggested: boolean;
  sort_order: number;
}

interface AiSuggestion {
  category: string;
  text: string;
  legal_reference: string | null;
  motivation: string;
}

export const SafetyRules: React.FC<Props> = ({ projectId }) => {
  const [docs, setDocs] = useState<RuleDoc[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [items, setItems] = useState<RuleItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [projectContext, setProjectContext] = useState("");
  const [suggestions, setSuggestions] = useState<AiSuggestion[]>([]);
  const [newCategory, setNewCategory] = useState("");
  const [newRule, setNewRule] = useState("");

  const loadDocs = async () => {
    const { data, error } = await supabase
      .from("safety_rule_documents")
      .select("id, name, status, project_specific, intro")
      .eq("project_id", projectId)
      .order("created_at", { ascending: true });
    if (error) toast.error("Kunde inte läsa skydds- och ordningsregler");
    const list = (data || []) as RuleDoc[];
    setDocs(list);
    setActiveId((prev) => prev ?? list[0]?.id ?? null);
    setLoading(false);
  };

  const loadItems = async (docId: string) => {
    const { data, error } = await supabase
      .from("safety_rule_items")
      .select("id, category, rule_key, text, generic_text, legal_reference, included, is_custom, ai_suggested, sort_order")
      .eq("document_id", docId)
      .order("sort_order", { ascending: true });
    if (error) toast.error("Kunde inte läsa reglerna");
    else setItems((data || []) as RuleItem[]);
  };

  const loadProjectContext = async () => {
    const { data } = await supabase
      .from("projects")
      .select("name, description, omfattning, syfte, bakgrund")
      .eq("id", projectId)
      .maybeSingle();
    if (!data) return;
    const parts = [
      data.name && `Projekt: ${data.name}`,
      data.description && `Beskrivning: ${data.description}`,
      data.omfattning && `Omfattning: ${data.omfattning}`,
      data.syfte && `Syfte: ${data.syfte}`,
      data.bakgrund && `Bakgrund: ${data.bakgrund}`,
    ].filter(Boolean);
    setProjectContext(parts.join("\n"));
  };

  useEffect(() => {
    setLoading(true);
    loadDocs();
    loadProjectContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (activeId) loadItems(activeId);
    else setItems([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const createDoc = async () => {
    setBusy(true);
    const { data: userData } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from("safety_rule_documents")
      .insert({
        project_id: projectId,
        name: "Skydds- och ordningsregler",
        created_by: userData.user?.id ?? null,
      })
      .select("id, name, status, project_specific, intro")
      .single();

    if (error || !data) {
      setBusy(false);
      return toast.error("Kunde inte skapa dokumentet");
    }

    const rows = SAFETY_RULES_TEMPLATE.flatMap((cat, ci) =>
      cat.rules.map((r, ri) => ({
        document_id: data.id,
        category: cat.category,
        rule_key: r.key,
        text: r.text,
        generic_text: r.text,
        legal_reference: r.legalReference ?? null,
        included: r.defaultIncluded !== false,
        sort_order: ci * 100 + ri,
      }))
    );
    const { error: itemErr } = await supabase.from("safety_rule_items").insert(rows);
    if (itemErr) toast.error("Kunde inte skapa reglerna");
    else toast.success("Skydds- och ordningsregler skapade (generisk mall)");

    setBusy(false);
    await loadDocs();
    setActiveId(data.id);
  };

  const updateDoc = async (patch: Partial<RuleDoc>) => {
    if (!activeId) return;
    setDocs((prev) => prev.map((d) => (d.id === activeId ? { ...d, ...patch } : d)));
    const { error } = await supabase.from("safety_rule_documents").update(patch).eq("id", activeId);
    if (error) toast.error("Kunde inte spara");
  };

  const deleteDoc = async (id: string) => {
    await supabase.from("safety_rule_documents").delete().eq("id", id);
    setActiveId(null);
    await loadDocs();
  };

  const updateItem = async (id: string, patch: Partial<RuleItem>) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
    const { error } = await supabase.from("safety_rule_items").update(patch).eq("id", id);
    if (error) toast.error("Kunde inte spara");
  };

  const removeItem = async (id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
    await supabase.from("safety_rule_items").delete().eq("id", id);
  };

  const addItem = async () => {
    if (!activeId || !newRule.trim()) return;
    const { error } = await supabase.from("safety_rule_items").insert({
      document_id: activeId,
      category: newCategory.trim() || "Övriga regler",
      rule_key: `custom-${Date.now()}`,
      text: newRule.trim(),
      is_custom: true,
      sort_order: 9000 + items.length,
    });
    if (error) return toast.error("Kunde inte lägga till regeln");
    setNewRule("");
    setNewCategory("");
    await loadItems(activeId);
  };

  const toggleCategory = async (category: string, included: boolean) => {
    const ids = items.filter((i) => i.category === category).map((i) => i.id);
    setItems((prev) => prev.map((i) => (ids.includes(i.id) ? { ...i, included } : i)));
    await supabase.from("safety_rule_items").update({ included }).in("id", ids);
  };

  const makeProjectSpecific = async () => {
    if (!activeId) return;
    const selected = items.filter((i) => i.included);
    if (!selected.length) return toast.error("Välj minst en regel först");

    setAiBusy(true);
    setSuggestions([]);
    try {
      const { data, error } = await supabase.functions.invoke("ai-safety-rules", {
        body: {
          projectContext,
          rules: selected.map((i) => ({ id: i.id, category: i.category, text: i.text })),
        },
      });

      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      void trackUsage("ai_generation", { documentType: "safety_rules", docNumber: "15" });

      const rewritten = ((data as any)?.rewritten || []) as Array<{ id: string; text: string }>;
      let changed = 0;
      for (const r of rewritten) {
        const current = items.find((i) => i.id === r.id);
        if (!current || !r.text?.trim() || r.text.trim() === current.text.trim()) continue;
        await updateItem(r.id, {
          text: r.text.trim(),
          generic_text: current.generic_text ?? current.text,
        });
        changed += 1;
      }

      setSuggestions(((data as any)?.suggested || []) as AiSuggestion[]);
      await updateDoc({ project_specific: true });
      toast.success(`${changed} regler anpassade till projektet`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "AI-anpassningen misslyckades");
    } finally {
      setAiBusy(false);
    }
  };

  const acceptSuggestion = async (s: AiSuggestion, index: number) => {
    if (!activeId) return;
    const { error } = await supabase.from("safety_rule_items").insert({
      document_id: activeId,
      category: s.category || "Projektspecifika regler",
      rule_key: `ai-${Date.now()}-${index}`,
      text: s.text,
      legal_reference: s.legal_reference,
      ai_suggested: true,
      sort_order: 8000 + index,
    });
    if (error) return toast.error("Kunde inte lägga till regeln");
    setSuggestions((prev) => prev.filter((_, i) => i !== index));
    await loadItems(activeId);
  };

  const restoreGeneric = async (item: RuleItem) => {
    if (!item.generic_text) return;
    await updateItem(item.id, { text: item.generic_text });
  };

  const activeDoc = docs.find((d) => d.id === activeId) || null;
  const included = items.filter((i) => i.included).length;
  const progress = items.length ? Math.round((included / items.length) * 100) : 0;
  const categories = useMemo(() => Array.from(new Set(items.map((i) => i.category))), [items]);
  const hasContext = projectContext.trim().length > 20;

  if (loading) return <div className="py-12 text-center text-muted-foreground">Laddar reglerna...</div>;

  return (
    <div className="space-y-4">
      <DocumentHeader
        docNumber="15"
        title="Skydds- och ordningsregler"
        description="Generiska regler som utgångspunkt – välj vilka som gäller och gör dem projektspecifika."
        icon={ShieldAlert}
        meta={
          activeDoc ? (
            <div className="flex items-center gap-4">
              <Badge variant={activeDoc.project_specific ? "default" : "outline"}>
                {activeDoc.project_specific ? "Projektspecifik" : "Generisk"}
              </Badge>
              <div className="w-40">
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>Valda regler</span>
                  <span>{included}/{items.length}</span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>
            </div>
          ) : null
        }
      />
      <Card>
        <CardContent className="space-y-4 pt-6">
          {docs.length === 0 ? (
            <div className="text-center py-10 space-y-3">
              <ShieldAlert className="h-8 w-8 mx-auto text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Inga skydds- och ordningsregler ännu. Skapa dokumentet med den generiska mallen.
              </p>
              <Button onClick={createDoc} disabled={busy}>
                {busy ? "Skapar..." : "Skapa skydds- och ordningsregler"}
              </Button>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                {docs.map((d) => (
                  <Button
                    key={d.id}
                    size="sm"
                    variant={d.id === activeId ? "default" : "outline"}
                    onClick={() => setActiveId(d.id)}
                  >
                    {d.name}
                  </Button>
                ))}
                <div className="ml-auto flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={createDoc} disabled={busy}>
                    <Plus className="h-4 w-4 mr-1" />Nytt regeldokument
                  </Button>
                </div>
              </div>

              {activeDoc && (
                <>
                  <div className="grid gap-2 sm:grid-cols-[1fr_200px]">
                    <Input
                      key={`n-${activeDoc.id}`}
                      defaultValue={activeDoc.name}
                      placeholder="Dokumentnamn"
                      onBlur={(e) => updateDoc({ name: e.target.value })}
                    />
                    <div className="flex gap-2">
                      {Object.keys(SAFETY_RULE_STATUS_LABELS).map((s) => (
                        <Button
                          key={s}
                          size="sm"
                          variant={activeDoc.status === s ? "default" : "outline"}
                          onClick={() => updateDoc({ status: s })}
                        >
                          {SAFETY_RULE_STATUS_LABELS[s]}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <Textarea
                    key={`i-${activeDoc.id}`}
                    rows={2}
                    placeholder="Inledande text (t.ex. vem reglerna gäller för och var de anslås)"
                    defaultValue={activeDoc.intro ?? ""}
                    onBlur={(e) => updateDoc({ intro: e.target.value })}
                  />

                  <div className="rounded-lg border border-border/60 bg-muted/30 p-4 space-y-2">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium">Gör reglerna projektspecifika</p>
                        <p className="text-xs text-muted-foreground">
                          {hasContext
                            ? "AI:n skriver om de valda reglerna utifrån projektets beskrivning och föreslår fler regler."
                            : "Lägg till en beskrivning av projektet i projektinställningarna för att kunna anpassa reglerna."}
                        </p>
                      </div>
                      <Button onClick={makeProjectSpecific} disabled={!hasContext || aiBusy}>
                        <Sparkles className="h-4 w-4 mr-1" />
                        {aiBusy ? "Anpassar..." : "Gör projektspecifika"}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {suggestions.length > 0 && (
        <Card className="border-primary/40">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Föreslagna extra regler ({suggestions.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="divide-y">
              {suggestions.map((s, i) => (
                <li key={`${s.text}-${i}`} className="py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{s.text}</p>
                    <p className="text-xs text-muted-foreground">
                      {s.category}
                      {s.legal_reference ? ` · ${s.legal_reference}` : ""}
                      {s.motivation ? ` · ${s.motivation}` : ""}
                    </p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" onClick={() => acceptSuggestion(s, i)}>
                      <Check className="h-4 w-4 mr-1" />Lägg till
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setSuggestions((prev) => prev.filter((_, x) => x !== i))}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {activeDoc &&
        categories.map((cat) => {
          const catItems = items.filter((i) => i.category === cat);
          const allIncluded = catItems.every((i) => i.included);
          return (
            <Card key={cat}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-3">
                  <CardTitle className="text-sm">{cat}</CardTitle>
                  <Button size="sm" variant="ghost" onClick={() => toggleCategory(cat, !allIncluded)}>
                    {allIncluded ? "Avmarkera alla" : "Markera alla"}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="divide-y">
                  {catItems.map((it) => (
                    <li key={it.id} className="py-3 flex items-start gap-3">
                      <Checkbox
                        checked={it.included}
                        onCheckedChange={(v) => updateItem(it.id, { included: v === true })}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0 space-y-1">
                        <Textarea
                          rows={2}
                          className={it.included ? "" : "opacity-60"}
                          defaultValue={it.text}
                          key={`t-${it.id}-${it.text.length}`}
                          onBlur={(e) => {
                            const v = e.target.value.trim();
                            if (v && v !== it.text) updateItem(it.id, { text: v });
                          }}
                        />
                        <div className="flex flex-wrap items-center gap-2">
                          {it.legal_reference && (
                            <Badge variant="outline" className="text-[10px]">{it.legal_reference}</Badge>
                          )}
                          {it.ai_suggested && <Badge variant="secondary" className="text-[10px]">AI-förslag</Badge>}
                          {it.is_custom && <Badge variant="secondary" className="text-[10px]">Egen regel</Badge>}
                          {it.generic_text && it.generic_text !== it.text && (
                            <Button size="sm" variant="ghost" onClick={() => restoreGeneric(it)}>
                              <Undo2 className="h-4 w-4 mr-1" />Återställ generisk
                            </Button>
                          )}
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removeItem(it.id)}>
                        <Trash2 className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          );
        })}

      {activeDoc && (
        <Card>
          <CardContent className="pt-6">
            <div className="grid gap-2 sm:grid-cols-[200px_1fr_auto]">
              <Input placeholder="Kategori" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} />
              <Input
                placeholder="Ny regel"
                value={newRule}
                onChange={(e) => setNewRule(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addItem()}
              />
              <Button onClick={addItem}><Plus className="h-4 w-4 mr-1" />Lägg till</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {activeDoc && docs.length > 1 && (
        <div className="flex justify-end">
          <Button variant="ghost" size="sm" onClick={() => deleteDoc(activeDoc.id)}>
            <Trash2 className="h-4 w-4 mr-1" />Ta bort dokumentet
          </Button>
        </div>
      )}
    </div>
  );
};

export default SafetyRules;
